import { useEffect, useRef, useState, useCallback } from 'react';
import Constants from "expo-constants";
import { AppState } from 'react-native';

const WS_URL = Constants.expoConfig?.extra?.WEBSOCKET_URL;

export function useWebSocket(channelName) {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const ws = useRef(null);
  const eventListeners = useRef({});

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    ws.current = new WebSocket(WS_URL);

    ws.current.onopen = () => {
      console.log('Connected to WebSocket server');
      setIsConnected(true);

      // Automatically subscribe to the specified channel
      if (channelName) {
        ws.current.send(
          JSON.stringify({
            action: 'subscribe',
            channel: channelName,
          })
        );
      }
    };

    ws.current.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);

        // Check if the message matches our channel
        if (payload.channel === channelName) {
          setMessages((prev) => [...prev, payload]);

          // Trigger registered event callbacks if any match
          if (payload.event && eventListeners.current[payload.event]) {
            eventListeners.current[payload.event](payload.data);
          }
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    ws.current.onclose = (e) => {
      console.log('WebSocket disconnected:', e.reason);
      setIsConnected(false);
      // Attempt auto-reconnect after 3 seconds
      setTimeout(connect, 3000);
    };

    ws.current.onerror = (e) => {
      console.error('WebSocket error:', e.message);
    };
  }, [channelName]);

  // Handle AppState changes (Reconnect when app comes back from background)
  useEffect(() => {
    connect();

    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'active' && (!ws.current || ws.current.readyState === WebSocket.CLOSED)) {
        connect();
      }
    });

    return () => {
      subscription.remove();
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [connect]);

  // Method to bind specific event callbacks (e.g., bind('status-changed', callback))
  const bind = useCallback((eventName, callback) => {
    eventListeners.current[eventName] = callback;
  }, []);

  // Method to send an event to the channel
  const trigger = useCallback(
    (eventName, data) => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(
          JSON.stringify({
            action: 'trigger',
            channel: channelName,
            event: eventName,
            data: data,
          })
        );
      } else {
        console.warn('Cannot trigger event: WebSocket is not connected.');
      }
    },
    [channelName]
  );

  return { isConnected, messages, trigger, bind };
}